import {
  ChatContainer,
  ConversationHeader,
  MessageInput,
  MessageList,
} from "@chatscope/chat-ui-kit-react";
import { useFindMany } from "@gadgetinc/react";
import {
  ArrowLeftIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  TrashIcon,
} from "@heroicons/react/16/solid";
import { LinkIcon, MicrophoneIcon } from "@heroicons/react/20/solid";
import { Icon, Button as PolarisButton, Text, Tooltip } from "@shopify/polaris";
import { CancelMinor, TickMinor, WandMajor } from "@shopify/polaris-icons";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { api } from "../../../api";
import { Button } from "../../../externalComponents/tailwind/button";
import { cleanFile } from "../../../functions/cleanFile";
import { generateMessageComponentFromContent } from "../../../functions/generateMessageComponentFromContent";
import { CustomerContext } from "../../../providers";
import { CustomerDetailsSidebar } from "../../CustomerDetailsSidebar";
import { BlockContactDialog } from "./BlockContactDialog";
import { ConversationSendTemplateDialog } from "./ConversationSendTemplateDialog";
import { SendAudioDialog } from "./SendAudioDialog";

const delay = (ms) => new Promise((res) => setTimeout(res, ms));

export function ConversationChat({
  conversation,
  getNextConversation,
  isMobile,
  setShowSidebar,
  showSidebar,
  showContactDetails,
  setShowContactDetails,
}) {
  const { user, customer } = useContext(CustomerContext);
  const [{ data, fetching }] = useFindMany(api.message, {
    first: 50,
    filter: [
      {
        conversationId: {
          equals: conversation.id,
        },
      },
      {
        failedAt: { isSet: false },
      },
    ],
    select: {
      id: true,
      direction: true,
      content: true,
      isQuickReply: true,
      image: { url: true },
      video: { url: true },
      voice: { url: true },
      document: { url: true },
      createdAt: true,
    },
    sort: { createdAt: "Descending" },

    live: true,
  });

  const [modalRef, setModalRef] = useState({
    blockContact: false,
    sendAudio: false,
    sendTemplate: false,
  });

  const openBlockContact = useCallback(
    () => setModalRef((_modalRef) => ({ ..._modalRef, blockContact: true })),
    [setModalRef],
  );

  const openSendTemplate = useCallback(
    () => setModalRef((_modalRef) => ({ ..._modalRef, sendTemplate: true })),
    [setModalRef],
  );

  const openSendAudio = useCallback(
    () => setModalRef((_modalRef) => ({ ..._modalRef, sendAudio: true })),
    [setModalRef],
  );

  const [allTemplatesOptions, setAllTemplatesOptions] = useState();
  const [allTemplatesData, setAllTemplatesData] = useState();
  const [selectedTemplate, setSelectedTemplate] = useState();
  const [template, setTemplate] = useState();
  const toggleActiveSendTemplate = useCallback(async () => {
    openSendTemplate();
    if (!allTemplatesOptions) {
      console.log("fetching templates");
      var templates = await api.getWhatsAppTemplates({
        limit: "5000",
      });
      // Remove templates with variables
      templates = (templates.waba_templates ?? templates).filter((t) => {
        const header = t.components.find((c) => c.type === "HEADER");
        if (
          header &&
          (header.format === "IMAGE" || header.format === "VIDEO")
        ) {
          return false;
        }
        const body = t.components.find((c) => c.type === "BODY");
        if (body && body.example) {
          return false;
        }
        const buttons = t.components.find((c) => c.type === "BUTTONS");
        buttons?.buttons?.forEach((b) => {
          if (b.type === "URL" && b.example) {
            return false;
          }
        });
        const carousel = t.components.find((c) => c.type === "CAROUSEL");
        if (carousel) {
          return false;
        }

        return true;
      });
      templates.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      setAllTemplatesData(templates);
      const allTemplates = templates?.map((t) => ({
        label: t.name,
        value: t.name,
      }));
      setAllTemplatesOptions(allTemplates);
      if (allTemplates?.length > 0) {
        setSelectedTemplate(allTemplates[0].value);
      }
    }
  }, [allTemplatesOptions]);

  useEffect(() => {
    setTemplate({});

    if (selectedTemplate && allTemplatesData) {
      const selectedTemplateData = allTemplatesData.find(
        (t) => t.name === selectedTemplate,
      );
      console.log(selectedTemplateData);
      const header = selectedTemplateData.components.find(
        (c) => c.type === "HEADER",
      );
      var headerParam = "";
      if (header && header.format === "IMAGE") {
        headerParam = "image";
      } else if (header && header.format === "VIDEO") {
        headerParam = "video";
      }

      const body = selectedTemplateData.components.find(
        (c) => c.type === "BODY",
      );
      const bodyText = body ? body.text : "";

      const footer = selectedTemplateData.components.find(
        (c) => c.type === "FOOTER",
      );
      const footerText = footer ? footer.text : "";

      const carousel = selectedTemplateData.components.find(
        (c) => c.type === "CAROUSEL",
      );

      const cards = carousel ? carousel.cards : null;

      setTemplate((prevTemplate) => ({
        ...prevTemplate,
        header: headerParam,
        body: bodyText,
        footer: footerText,
        cards: cards,
      }));

      const buttons = selectedTemplateData.components.find(
        (c) => c.type === "BUTTONS",
      );
      let quickReplies = [];
      buttons?.buttons?.forEach((b, index) => {
        if (b.type === "URL") {
          setTemplate((prevTemplate) => ({
            ...prevTemplate,
            urlButton: b.text,
            isUrlButtonAtBottom: index === buttons.buttons.length - 1,
          }));
        } else {
          quickReplies.push(b.text);
        }
      });

      setTemplate((prevTemplate) => ({
        ...prevTemplate,
        quickReply1: quickReplies[0] || "",
        quickReply2: quickReplies[1] || "",
        quickReply3: quickReplies[2] || "",
      }));
    }
  }, [selectedTemplate]);

  const [suggestion, setSuggestion] = useState("");
  const [aiSuggestionId, setAiSuggestionId] = useState("");
  const [isSuggestionLoading, setIsSuggestionLoading] = useState(false);
  const generateSuggestion = async () => {
    setIsSuggestionLoading(true);
    const newSuggestion = await api.generateCopilotAnswer({
      conversationId: conversation.id,
      customerId: user.customerId,
      contactId: conversation.contact.id,
    });
    if (newSuggestion && newSuggestion.suggestion) {
      setSuggestion(newSuggestion.suggestion);
      setAiSuggestionId(newSuggestion.aiSuggestionId);
    } else {
      setSuggestion("Nothing relevant was generated...");
      await delay(1000);
      setSuggestion("");
    }
    setIsSuggestionLoading(false);
  };
  const acceptSuggestion = async () => {
    setMessageValue(suggestion);
    setSuggestion("");
    await api.aiSuggestion.update(aiSuggestionId, {
      isSuggestionAccepted: true,
    });
  };
  const rejectSuggestion = async () => {
    setSuggestion("");
    await api.aiSuggestion.update(aiSuggestionId, {
      isSuggestionAccepted: false,
    });
  };

  const [messages, setMessages] = useState();
  useEffect(() => {
    if (!fetching) {
      setMessages([...data].reverse());
    }
  }, [data]);

  const inputRef = useRef();
  const inputAttachRef = useRef(null);

  const handleSend = async (message) => {
    message = message.replaceAll("<br>", "\n").replaceAll("&nbsp;", "");
    setMessages([
      ...messages,
      {
        content: { type: "text", text: { body: message } },
        id: new Date().toISOString(),
      },
    ]);
    setMessageValue("");
    if (aiSuggestionId !== "") {
      // await api.aiSuggestion.update(aiSuggestionId, {
      //   messageAfterSuggestion: message,
      // });
      setAiSuggestionId("");
    }
    await api.sendWhatsAppTextMessage({
      contactNumber: conversation.contact.userPhoneNumber,
      bsuid: conversation.contact.bsuid,
      body: message,
    });
    inputRef.current?.focus();
  };

  const [isTogglingConversation, setIsTogglingConversation] = useState(false);
  const [messageValue, setMessageValue] = useState("");

  const toggleConversation = async () => {
    setIsTogglingConversation(true);
    await api.conversation.update(conversation.id, {
      conversation: {
        isOpened: !conversation.isOpened,
      },
    });
    setIsTogglingConversation(false);
    getNextConversation();
  };

  const onAttachClick = async () => {
    // Trigger file input click event
    inputAttachRef.current.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];

    if (!file) {
      return;
    }

    const response = await api.media.create({
      customer: { _link: user.customerId },
      file: {
        file: cleanFile(file),
      },
      type: file.type === "application/pdf" ? "document" : "image",
    });
    console.log("file uploaded");

    if (response?.file?.url) {
      await api.sendWhatsappFile({
        contactNumber: conversation.contact.userPhoneNumber,
        bsuid: conversation.contact.bsuid,
        fileUrl: response?.file?.url,
        fileType: file.type === "application/pdf" ? "document" : "image",
        fileName: file.name,
      });
      console.log("message sent");
    }
  };

  return (
    conversation && (
      <>
        <ChatContainer key="chat">
          <ConversationHeader
            className={isMobile ? "cs-conversation-header-mobile" : ""}
          >
            <ConversationHeader.Content
              userName={
                <div className="flex justify-start items-center">
                  {isMobile && (
                    <ArrowLeftIcon
                      className="h-5 mr-2"
                      onClick={() => {
                        setShowSidebar(true);
                        setShowContactDetails(false);
                      }}
                    />
                  )}
                  <Link
                    to={`/contacts/${conversation?.contact?.id}`}
                    className="hover:text-pink hover:underline"
                  >
                    {conversation.contact?.whatsappName ??
                      (conversation.contact?.shopifyCustomer?.firstName ?? "") +
                        " " +
                        (conversation.contact?.shopifyCustomer?.lastName ?? "")}
                  </Link>
                </div>
              }
              info={
                Math.abs(conversation?.lastInboundMessageAt - new Date()) /
                  36e5 <
                24 ? (
                  ""
                ) : (
                  <div
                    className="cursor-pointer bg-white flex items-center content-end flex-wrap text-zinc-500 hover:text-zinc-900 hover:underline w-full"
                    onClick={() =>
                      window.open(
                        "https://help.getsimio.com/fr_FR/introduction/quest-ce-quune-conversation-whatsapp-",
                        "_blank",
                      )
                    }
                    style={{
                      position: isMobile ? "fixed" : null,
                      maxWidth: isMobile ? "100%" : null,
                    }}
                  >
                    <LinkIcon className="w-3 h-3 mr-1" />
                    <p>24h window closed, you cannot send messages.</p>
                  </div>
                )
              }
            />
            <ConversationHeader.Actions>
              <div
                className={
                  isMobile
                    ? "flex flex-raw h-full mt-3 items-start"
                    : "flex flex-raw items-center"
                }
              >
                {Math.abs(conversation?.lastInboundMessageAt - new Date()) /
                  36e5 <
                  24 && (
                  <div style={{ marginRight: "5px" }}>
                    <Tooltip
                      content="Send a voice note"
                      {...(isMobile && { active: false })}
                    >
                      <Button onClick={openSendAudio} color="dark">
                        <div
                          style={{ height: `${isMobile ? "10px" : "20px"}` }}
                          className="flex justify-center items-center"
                        >
                          <MicrophoneIcon className="h-4" />
                        </div>
                      </Button>
                    </Tooltip>
                  </div>
                )}
                <div style={{ marginRight: "5px" }}>
                  <Tooltip
                    content={`Block ${
                      conversation.contact?.whatsappName ?? "this contact"
                    }`}
                    {...(isMobile && { active: false })}
                  >
                    <Button onClick={openBlockContact} color="optionButton">
                      <div
                        style={{ height: `${isMobile ? "10px" : "20px"}` }}
                        className="flex justify-center items-center"
                      >
                        <TrashIcon className="h-4 text-gray-600" />
                      </div>
                    </Button>
                  </Tooltip>
                </div>
                <Tooltip
                  content={`Move to ${
                    conversation.isOpened ? "Closed" : "Opened"
                  } conversations`}
                  {...(isMobile && { active: false })}
                >
                  <Button
                    loading={isTogglingConversation}
                    color="optionButton"
                    onClick={toggleConversation}
                  >
                    <div
                      style={{ height: `${isMobile ? "10px" : "20px"}` }}
                      className="flex justify-center items-center"
                    >
                      <CheckIcon className="h-4 text-gray-600" />
                      <Text></Text>
                      {conversation?.isOpened ? "Close" : "Reopen"}
                    </div>
                  </Button>
                </Tooltip>
                <div style={{ marginRight: "5px" }}>
                  {isMobile && (
                    <div className="fixed top-10 right-2">
                      <Button
                        onClick={() =>
                          setShowContactDetails(!showContactDetails)
                        }
                        color="optionButton"
                        style={{ height: "10px" }}
                      >
                        <div
                          style={{ height: "10px" }}
                          className="flex justify-center items-center"
                        >
                          {showContactDetails ? (
                            <ChevronDownIcon className="h-3" />
                          ) : (
                            <ChevronUpIcon className="h-3" />
                          )}
                        </div>
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </ConversationHeader.Actions>
          </ConversationHeader>

          <MessageList loading={fetching} autoScrollToBottomOnMount={true}>
            {messages?.map((m) => generateMessageComponentFromContent(m))}
          </MessageList>

          {Math.abs(conversation?.lastInboundMessageAt - new Date()) / 36e5 <
            24 && (
            <MessageInput
              className="relative p-2"
              placeholder="Write a message"
              onSend={handleSend}
              sendDisabled={messageValue?.replaceAll(" ", "") === ""}
              ref={inputRef}
              onChange={(val) => setMessageValue(val)}
              value={messageValue}
              onPaste={onPasteMessage}
              onAttachClick={onAttachClick}
              autoFocus={!showSidebar ? true : false}
            >
              <div
                style={{
                  position: "absolute",
                  bottom: "100%",
                  right: "0",
                  zIndex: 10,
                  display: "flex",
                  alignItems: "end",
                  justifyContent: "flex-end",
                  margin: "10px",
                  backgroundColor: "transparent",
                }}
              >
                {suggestion && (
                  <span
                    style={{
                      marginRight: "10px",
                      padding: "5px 10px",
                      borderRadius: ".7em",
                      backgroundColor: "#3f3f3f",
                      boxShadow: "0 0 10px -5px #22184B",
                      color: "#f1f1f1",
                    }}
                  >
                    <Text variant="bodyMd">{suggestion}</Text>
                  </span>
                )}
                {suggestion === "" ? (
                  <Tooltip
                    hoverDelay={1000}
                    content={
                      customer.isGaiaActivated
                        ? "Generate answer with AI Copilot"
                        : "Want to generate answers with AI Copilot? Ask support (it's free)"
                    }
                  >
                    <PolarisButton
                      variant="primary"
                      onClick={
                        customer.isGaiaActivated ? generateSuggestion : () => {}
                      }
                      loading={isSuggestionLoading}
                    >
                      <Icon source={WandMajor} />
                    </PolarisButton>
                  </Tooltip>
                ) : (
                  <>
                    <PolarisButton variant="primary" onClick={acceptSuggestion}>
                      <Icon source={TickMinor} />
                    </PolarisButton>
                    <PolarisButton variant="primary" onClick={rejectSuggestion}>
                      <Icon source={CancelMinor} />
                    </PolarisButton>
                  </>
                )}
              </div>
            </MessageInput>
          )}

          {Math.abs(conversation?.lastInboundMessageAt - new Date()) / 36e5 >=
            24 && (
            <div
              // Hack needed because we juste want the button. BTW ChatContainer needs a MessageInput
              // eslint-disable-next-line react/no-unknown-property
              as={MessageInput}
              className="w-full flex justify-center py-2 border-t-[1px]"
            >
              <Button color="primaryButton" onClick={toggleActiveSendTemplate}>
                Send a template
              </Button>
            </div>
          )}
        </ChatContainer>
        <input
          type="file"
          style={{ display: "none" }}
          ref={inputAttachRef}
          onChange={handleFileChange}
          accept="image/*,application/pdf"
        />

        {!isMobile && (
          <CustomerDetailsSidebar
            conversation={conversation}
            isMobile={isMobile}
          />
        )}
        {isMobile && showContactDetails && (
          <div className="fixed top-1 w-full h-full bg-white flex justify-center pt-20 overscroll-x-none overflow-hidden">
            <CustomerDetailsSidebar
              conversation={conversation}
              isMobile={isMobile}
            />
          </div>
        )}

        <BlockContactDialog
          conversationId={conversation.id}
          modalRef={modalRef}
          setModalRef={setModalRef}
          whatsappName={conversation?.contact?.whatsappName}
          getNextConversation={getNextConversation}
        />

        <ConversationSendTemplateDialog
          customerId={customer.id}
          allTemplatesOptions={allTemplatesOptions}
          selectedTemplate={selectedTemplate}
          setSelectedTemplate={setSelectedTemplate}
          template={template}
          allTemplatesData={allTemplatesData}
          phoneNumber={conversation.contact?.userPhoneNumber}
          bsuid={conversation.contact?.bsuid}
          modalRef={modalRef}
          setModalRef={setModalRef}
        />

        <SendAudioDialog
          modalRef={modalRef}
          setModalRef={setModalRef}
          customerId={user.customerId}
          phoneNumber={conversation.contact?.userPhoneNumber}
          bsuid={conversation.contact?.bsuid}
          whatsappName={conversation.contact?.whatsappName}
        />
      </>
    )
  );

  function onPasteMessage(event) {
    console.log(event);
    event.preventDefault();
    let paste = (event.clipboardData || window.clipboardData).getData("text");
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    selection.deleteFromDocument();
    selection.getRangeAt(0).insertNode(document.createTextNode(paste));
    selection.collapseToEnd();
    setMessageValue(selection.anchorNode.textContent);
  }
}
